import React, { useState, useRef, useEffect } from 'react';
import { gregorianToJalali, jalaliToGregorian, formatJalaliDate, parseJalaliDate, getCurrentJalaliDate } from '../../utils/jalaliDate';
import './JalaliDatePicker.css';

const JalaliDatePicker = ({ value, onChange, disabled = false, placeholder = 'تاریخ را انتخاب کنید', showTime = false }) => {
    console.log('JalaliDatePicker value:', value);
    const today = getCurrentJalaliDate();

    const [isOpen, setIsOpen] = useState(false);
    const [displayValue, setDisplayValue] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(today.month);
    const [currentYear, setCurrentYear] = useState(today.year);
    const [selectedHour, setSelectedHour] = useState(0);
    const [selectedMinute, setSelectedMinute] = useState(0);
    const pickerRef = useRef(null);
    const calendarRef = useRef(null);

    const jalaliMonths = [
        'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
        'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
    ];

    useEffect(() => {
        console.log('value received:', value);
        if (value) {
            console.log('value received:', value);
            const parsed = parseJalaliDate(value);
            console.log('parsed result:', parsed);
            if (parsed) {
                setSelectedDate(parsed);
                setCurrentMonth(parsed.month);
                setCurrentYear(parsed.year);
                if (parsed.hour !== undefined) setSelectedHour(parsed.hour);
                if (parsed.minute !== undefined) setSelectedMinute(parsed.minute);
                setDisplayValue(value);
            }
        }
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && pickerRef.current && calendarRef.current) {
            const inputRect = pickerRef.current.getBoundingClientRect();
            const calendar = calendarRef.current;
            const viewportHeight = window.innerHeight;
            const calendarHeight = calendar.offsetHeight || 400;
            const spaceBelow = viewportHeight - inputRect.bottom;
            const spaceAbove = inputRect.top;

            if (spaceBelow < calendarHeight && spaceAbove > spaceBelow) {
                calendar.style.top = `${inputRect.top - calendarHeight - 4}px`;
                calendar.style.left = `${inputRect.left}px`;
            } else {
                calendar.style.top = `${inputRect.bottom + 4}px`;
                calendar.style.left = `${inputRect.left}px`;
            }
        }
    }, [isOpen]);

    const getDaysInMonth = (year, month) => {
        if (month <= 6) return 31;
        if (month <= 11) return 30;
        const isLeap = ((year - 474) % 2820 + 474 + 38) % 2816 < 682;
        return isLeap ? 30 : 29;
    };

    const getFirstDayOfMonth = (year, month) => {
        const greg = jalaliToGregorian(year, month, 1);
        const date = new Date(greg.year, greg.month - 1, greg.day);
        return (date.getDay() + 1) % 7;
    };

    const handleDateSelect = (day) => {
        const newDate = { year: currentYear, month: currentMonth, day };
        setSelectedDate(newDate);

        let formatted;
        if (showTime) {
            formatted = formatJalaliDate(currentYear, currentMonth, day, selectedHour, selectedMinute);
        } else {
            formatted = formatJalaliDate(currentYear, currentMonth, day);
        }

        setDisplayValue(formatted);
        onChange(formatted);
        if (!showTime) setIsOpen(false);
    };

    const handleTimeChange = (type, val) => {
        const newVal = parseInt(val, 10);
        if (type === 'hour') setSelectedHour(newVal);
        else setSelectedMinute(newVal);

        if (selectedDate) {
            const h = type === 'hour' ? newVal : selectedHour;
            const m = type === 'minute' ? newVal : selectedMinute;
            const formatted = formatJalaliDate(selectedDate.year, selectedDate.month, selectedDate.day, h, m);
            setDisplayValue(formatted);
            onChange(formatted);
        }
    };

    const handlePrevMonth = () => {
        if (currentMonth === 1) {
            setCurrentMonth(12);
            setCurrentYear(currentYear - 1);
        } else {
            setCurrentMonth(currentMonth - 1);
        }
    };

    const handleNextMonth = () => {
        if (currentMonth === 12) {
            setCurrentMonth(1);
            setCurrentYear(currentYear + 1);
        } else {
            setCurrentMonth(currentMonth + 1);
        }
    };

    const handleToday = () => {
        const today = getCurrentJalaliDate();
        const now = new Date();
        const h = now.getHours();
        const m = now.getMinutes();

        setSelectedDate(today);
        setCurrentMonth(today.month);
        setCurrentYear(today.year);
        if (showTime) {
            setSelectedHour(h);
            setSelectedMinute(m);
        }

        let formatted;
        if (showTime) {
            formatted = formatJalaliDate(today.year, today.month, today.day, h, m);
        } else {
            formatted = formatJalaliDate(today.year, today.month, today.day);
        }

        setDisplayValue(formatted);
        onChange(formatted);
        if (!showTime) setIsOpen(false);
    };

    const renderCalendar = () => {
        const daysInMonth = getDaysInMonth(currentYear, currentMonth);
        const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
        const days = [];

        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="jalali-day empty"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const isSelected = selectedDate &&
                selectedDate.year === currentYear &&
                selectedDate.month === currentMonth &&
                selectedDate.day === day;

            const today = getCurrentJalaliDate();
            const isToday = today.year === currentYear &&
                today.month === currentMonth &&
                today.day === day;

            days.push(
                <div
                    key={day}
                    className={`jalali-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
                    onClick={() => handleDateSelect(day)}
                >
                    {day}
                </div>
            );
        }

        return days;
    };

    const handleInputChange = (e) => {
        let raw = e.target.value.replace(/[^0-9]/g, '');
        let formatted = '';

        if (showTime) {
            if (raw.length > 0) {
                formatted += raw.substring(0, 2);
            }
            if (raw.length > 2) {
                formatted += ':' + raw.substring(2, 4);
            }
            if (raw.length > 4) {
                formatted += ' ' + raw.substring(4, 8);
            }
            if (raw.length > 8) {
                formatted += '/' + raw.substring(8, 10);
            }
            if (raw.length > 10) {
                formatted += '/' + raw.substring(10, 12);
            }
        } else {
            if (raw.length > 0) {
                formatted += raw.substring(0, 4);
            }
            if (raw.length > 4) {
                formatted += '/' + raw.substring(4, 6);
            }
            if (raw.length > 6) {
                formatted += '/' + raw.substring(6, 8);
            }
        }

        setDisplayValue(formatted);
        onChange(formatted);
        
        const parsed = parseJalaliDate(formatted);
        if (parsed) {
            setSelectedDate(parsed);
            setCurrentMonth(parsed.month);
            setCurrentYear(parsed.year);
            if (parsed.hour !== undefined) setSelectedHour(parsed.hour);
            if (parsed.minute !== undefined) setSelectedMinute(parsed.minute);
        }
    };

    return (
        <div className="jalali-datepicker" ref={pickerRef}>
            <input
                type="text"
                className="form-control"
                value={displayValue}
                onClick={() => !disabled && setIsOpen(true)}
                onChange={handleInputChange}
                onBlur={(e) => {
                    const parsed = parseJalaliDate(e.target.value);
                    if (parsed) {
                        onChange(e.target.value);
                    }
                }}
                placeholder={placeholder}
                disabled={disabled}
                dir="ltr"
            />

            {isOpen && !disabled && (
                <div className="jalali-calendar" ref={calendarRef}>
                    <div className="jalali-header">
                        <button type="button" onClick={handlePrevMonth} className="btn btn-sm">
                            <i className="feather-chevron-right"></i>
                        </button>
                        <div className="jalali-month-year">
                            {jalaliMonths[currentMonth - 1]} {currentYear}
                        </div>
                        <button type="button" onClick={handleNextMonth} className="btn btn-sm">
                            <i className="feather-chevron-left"></i>
                        </button>
                    </div>

                    <div className="jalali-weekdays">
                        <div>ش</div>
                        <div>ی</div>
                        <div>د</div>
                        <div>س</div>
                        <div>چ</div>
                        <div>پ</div>
                        <div>ج</div>
                    </div>

                    <div className="jalali-days">
                        {renderCalendar()}
                    </div>

                    {showTime && (
                        <div className="jalali-time-picker border-top pt-2 mt-2">
                            <div className="d-flex justify-content-center align-items-center gap-2" dir="ltr">
                                <select
                                    className="form-select form-select-sm w-auto"
                                    value={selectedHour}
                                    onChange={(e) => handleTimeChange('hour', e.target.value)}
                                >
                                    {[...Array(24).keys()].map(h => (
                                        <option key={h} value={h}>{h.toString().padStart(2, '0')}</option>
                                    ))}
                                </select>
                                :
                                <select
                                    className="form-select form-select-sm w-auto"
                                    value={selectedMinute}
                                    onChange={(e) => handleTimeChange('minute', e.target.value)}
                                >
                                    {[...Array(60).keys()].map(m => (
                                        <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    <div className="jalali-footer">
                        <button type="button" onClick={handleToday} className="btn btn-sm btn-primary">
                            امروز
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JalaliDatePicker;
